import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Home as HomeIcon, Shield, Users, Clock, Award, Star, ChevronDown, 
  CheckCircle, FileText, Wrench, ClipboardCheck, Phone, Mail, 
  Calendar, ThumbsUp, BadgeCheck, X, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [reviews, setReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, text: '' });
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  // Lock body scroll when modal is open (fixes iOS Safari cursor bug)
  useEffect(() => {
    if (showReviewModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showReviewModal]);

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${API}/api/reviews`);
      const data = await res.json();
      setReviews(data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    try {
      const res = await fetch(`${API}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewForm)
      });
      if (res.ok) {
        setSubmitStatus('success');
        setReviewForm({ name: '', rating: 5, text: '' });
        setTimeout(() => {
          setShowReviewModal(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      setSubmitStatus('error');
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen">
      {/* Review Submission Modal */}
      {showReviewModal && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" 
          onClick={() => setShowReviewModal(false)}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="min-h-full flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setShowReviewModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
            >
              <X size={24} />
            </button>
            <h3 className="text-2xl font-bold text-[#003366] mb-6">Leave a Review</h3>
            
            {submitStatus === 'success' ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-700">Thank you for your review!</p>
                <p className="text-gray-500">It will appear after approval.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitReview} className="space-y-5">
                <div>
                  <label htmlFor="reviewName" className="block text-gray-700 font-semibold mb-2">Your Name</label>
                  <input
                    id="reviewName"
                    type="text"
                    required
                    value={reviewForm.name}
                    onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                    className="block w-full border border-gray-300 rounded-lg px-4 py-3"
                    placeholder="John D."
                    autoComplete="name"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Rating</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({...reviewForm, rating: star})}
                        className="p-1"
                      >
                        <Star 
                          size={28} 
                          className={star <= reviewForm.rating ? 'text-[#CFB973] fill-[#CFB973]' : 'text-gray-300'} 
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="reviewText" className="block text-gray-700 font-semibold mb-2">Your Review</label>
                  <textarea
                    id="reviewText"
                    required
                    value={reviewForm.text}
                    onChange={(e) => setReviewForm({...reviewForm, text: e.target.value})}
                    rows={4}
                    className="block w-full border border-gray-300 rounded-lg px-4 py-3 resize-none"
                    placeholder="Share your experience with Valenrock..."
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#003366] hover:bg-[#002244] text-white py-3"
                  disabled={submitStatus === 'submitting'}
                >
                  {submitStatus === 'submitting' ? 'Submitting...' : 'Submit Review'}
                </Button>
                {submitStatus === 'error' && (
                  <p className="text-red-500 text-center mt-2">Something went wrong. Please try again.</p>
                )}
              </form>
            )}
          </div>
          </div>
        </div>
      )}

      {/* Hero Section - Premium Ad Style */}
      <section className="relative min-h-[650px] md:min-h-[700px] flex items-center justify-center overflow-hidden mt-20">
        {/* Background with overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/home-hero.jpg)' }}
        >
          <div className="absolute inset-0 bg-[#003366]/90"></div>
          {/* Blueprint pattern overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
            backgroundSize: '100px 100px'
          }}></div>
        </div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" 
            alt="" 
            className="w-[400px] md:w-[500px] h-auto opacity-[0.06]"
          />
        </div>
        
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
          {/* Main Headline */}
          <h1 className="mb-8">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              Your Home's Most
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Trusted Advocate.</span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-6 max-w-2xl leading-relaxed">
            Expert whole-home assessments and proactive care plans designed to protect your <span className="text-[#CFB973] font-semibold">greatest investment</span>.
          </p>
          
          {/* Visual Hook Badge */}
          <div className="bg-white/10 backdrop-blur-sm border border-[#CFB973]/30 rounded-full px-6 py-3 mb-8 inline-flex items-center gap-3">
            <span className="text-[#CFB973] text-lg">✦</span>
            <span className="text-white/90 text-sm md:text-base">
              Most inspections end with a report. <span className="text-[#CFB973] font-semibold">Ours begins with one.</span>
            </span>
            <span className="text-[#CFB973] text-lg">✦</span>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all">
                Join the Waitlist
              </Button>
            </Link>
            <Link to="/remodeling">
              <Button size="lg" className="bg-transparent hover:bg-white/10 text-white font-semibold px-10 py-7 text-lg border-2 border-[#CFB973]">
                Remodeling Services
              </Button>
            </Link>
          </div>
          
          <p className="text-white/70 text-sm">
            <span className="block sm:inline">Assessments: Waitlist Only</span>
            <span className="hidden sm:inline"> • </span>
            <span className="block sm:inline">Remodeling: Now&nbsp;Available</span>
          </p>
        </div>
        
        {/* Scroll indicator */}
        <button
          onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-8 left-0 right-0 mx-auto w-10 h-10 animate-bounce cursor-pointer hover:scale-110 transition-transform z-20"
          aria-label="Scroll to learn more"
        >
          <div className="w-10 h-10 rounded-full border-2 border-[#CFB973] flex items-center justify-center">
            <ChevronDown className="text-[#CFB973] w-6 h-6" />
          </div>
        </button>
      </section>

      {/* Trust Badges - Gold Circles */}
      <section className="py-8 bg-[#E8EEF3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {/* Licensed & Insured */}
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">Licensed & Insured</p>
              <p className="text-[#003366]/60 text-xs">#13VH13590600</p>
            </div>
            
            {/* 100% Satisfaction */}
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <ThumbsUp className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">100% Satisfaction</p>
              <p className="text-[#003366]/60 text-xs">Guarantee</p>
            </div>
            
            {/* 10+ Years */}
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Award className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">10+ Years</p>
              <p className="text-[#003366]/60 text-xs">Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Valenrock Difference - SEO Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#CFB973] font-semibold text-sm uppercase tracking-wider mb-2">The Valenrock Difference</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">
                Proactive Protection for Your Greatest&nbsp;Investment
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                At Valenrock™ Home Services, we believe the best way to maintain a home is to stay ahead of the issues before they become costly repairs. Our signature <strong>Whole-Home Assessment &amp;&nbsp;Care&nbsp;Plan</strong> provides homeowners across <strong>Northern New&nbsp;Jersey</strong> and <strong>Middlesex&nbsp;County</strong> with a comprehensive roadmap for property&nbsp;health.
              </p>
              <p className="text-gray-700 leading-relaxed mb-8">
                From foundational checks to systems efficiency, our expert technicians conduct a top-to-bottom evaluation tailored to the specific climate and housing styles of the Garden&nbsp;State. Whether you're in <strong>Bergen, Morris, or Essex&nbsp;County</strong>, our mission is to provide peace of mind through professional&nbsp;oversight.
              </p>
              <Link to="/assessment">
                <Button className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-8 py-6 text-lg">
                  Learn About Our Assessment
                </Button>
              </Link>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/i42bnqzj_IMG_3442.jpeg" 
                alt="Valenrock team consulting with homeowners" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - 4 Steps with Gold Icons */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">How Valenrock Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We start with a Whole-Home Assessment — a thorough walkthrough that documents your home's condition and creates your personalized Home Profile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <Phone className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  1
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Connect</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Reach out and tell us about your home and concerns.</p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <Calendar className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  2
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Book Your Assessment</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Schedule a time that works for you.</p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <ClipboardCheck className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  3
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Get Your Home Profile</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Receive a detailed report within 48 hours.</p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <CheckCircle className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  4
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Your Choice</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Plan maintenance, repairs, or renovations on your timeline.</p>
            </div>
          </div>

          {/* Tagline */}
          <div className="text-center mt-12 py-6 border-t-2 border-b-2 border-[#CFB973]/30">
            <p className="text-xl md:text-2xl text-[#003366] font-medium italic">
              "No subscription. No pressure. Just clarity and a plan built around your goals."
            </p>
          </div>
        </div>
      </section>

      {/* What's in Your Home Profile - Navy Background */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What's in Your Home Profile?</h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Think of it as a living record of your home — updated whenever you choose future work with us.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { icon: ClipboardCheck, title: 'Current Condition', desc: 'All major systems' },
              { icon: FileText, title: 'Priority Issues', desc: 'Ranked by urgency' },
              { icon: Calendar, title: 'Repair Timeline', desc: 'Recommended schedule' },
              { icon: Wrench, title: 'Appliance Info', desc: 'Brand & model data' },
              { icon: HomeIcon, title: 'Future Projects', desc: 'Ideas & budgets' },
              { icon: Shield, title: 'Securely Stored', desc: 'Updated over time' },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <Icon className="w-8 h-8 text-[#003366]" />
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                  <p className="text-white/60 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/assessment">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-6 text-lg shadow-xl">
                Learn More About the Assessment
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section - Light Background */}
      <section className="py-20 bg-[#E8EEF3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">Simple, Transparent Pricing</h2>
              <p className="text-gray-600 mb-6">
                No subscriptions. No hidden fees. One-time assessment fee — that's it.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#003366]" />
                  </div>
                  <span className="text-gray-700">Complete walkthrough of your home</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#003366]" />
                  </div>
                  <span className="text-gray-700">Detailed Home Profile report</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#003366]" />
                  </div>
                  <span className="text-gray-700">Priority recommendations & timeline</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#003366]" />
                  </div>
                  <span className="text-gray-700">100% satisfaction guarantee</span>
                </li>
              </ul>
            </div>

            {/* Pricing Card */}
            <div className="bg-white p-8 rounded-2xl shadow-2xl border-4 border-[#CFB973]">
              <div className="text-center mb-6">
                <p className="text-[#003366]/60 text-sm font-medium mb-2">WHOLE-HOME ASSESSMENT</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-lg text-gray-500">Starting at</span>
                  <span className="text-5xl font-bold text-[#003366]">$249</span>
                </div>
                <p className="text-gray-500 text-sm mt-2">One-time fee</p>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Single-Family Home</span>
                  <span className="font-bold text-[#003366]">$249+</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Multi-Unit Property</span>
                  <span className="font-bold text-[#003366]">$449+</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Large Estate (4,000+ sqft)</span>
                  <span className="font-bold text-[#003366]">$649+</span>
                </div>
              </div>
              
              <Link to="/contact">
                <Button className="w-full bg-[#003366] hover:bg-[#003366]/90 text-white py-6 text-lg font-bold shadow-lg">
                  Book Your Assessment
                </Button>
              </Link>
              <p className="text-gray-400 text-xs text-center mt-4">Final pricing confirmed after a brief phone consultation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Premium Style */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">What Homeowners Say</h2>
            <p className="text-gray-600 mb-6">Real feedback from real clients in Northern & Central NJ.</p>
            <Button 
              onClick={() => setShowReviewModal(true)}
              className="bg-[#CFB973] hover:bg-[#b8a55e] text-[#003366] font-semibold"
            >
              <Star className="mr-2" size={18} /> Leave a Review
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Default Testimonial 1 */}
            <div className="bg-[#E8EEF3] rounded-2xl p-8 relative">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="text-[#CFB973] fill-[#CFB973]" size={20} />)}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "Finally, someone who explains what my home actually needs — without trying to upsell me. The Home Profile gave me a clear roadmap."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold text-xl">
                  SM
                </div>
                <div>
                  <p className="font-bold text-[#003366]">Sarah M.</p>
                  <p className="text-gray-500 text-sm">Montclair, NJ</p>
                </div>
              </div>
            </div>

            {/* Default Testimonial 2 */}
            <div className="bg-[#E8EEF3] rounded-2xl p-8 relative">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="text-[#CFB973] fill-[#CFB973]" size={20} />)}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "As a first-time homeowner, I had no idea what to prioritize. Valenrock made it simple. Worth every penny."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold text-xl">
                  JD
                </div>
                <div>
                  <p className="font-bold text-[#003366]">John D.</p>
                  <p className="text-gray-500 text-sm">Edison, NJ</p>
                </div>
              </div>
            </div>

            {/* Default Testimonial 3 */}
            <div className="bg-[#E8EEF3] rounded-2xl p-8 relative">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="text-[#CFB973] fill-[#CFB973]" size={20} />)}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "Responsive, honest, and no subscription traps. Exactly what I was looking for. Highly recommend!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold text-xl">
                  MR
                </div>
                <div>
                  <p className="font-bold text-[#003366]">Michael R.</p>
                  <p className="text-gray-500 text-sm">Summit, NJ</p>
                </div>
              </div>
            </div>

            {/* Approved Reviews from Database */}
            {reviews.map((review) => (
              <div key={review.id} className="bg-[#E8EEF3] rounded-2xl p-8 relative">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={i < review.rating ? "text-[#CFB973] fill-[#CFB973]" : "text-gray-300"} 
                      size={20} 
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{review.text}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold text-xl">
                    {getInitials(review.name)}
                  </div>
                  <div>
                    <p className="font-bold text-[#003366]">{review.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">See How We Help Homeowners</h2>
            <p className="text-gray-600">From first-time buyers to long-time homeowners — we handle the maintenance so you can enjoy your home.</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#003366]">
            <video 
              controls
              poster=""
              className="w-full aspect-video bg-black"
              preload="metadata"
            >
              <source src="https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/reuln3vy_1375966.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Why Not Alternatives - Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">Why Not Just Call a Contractor?</h2>
            <p className="text-gray-600">Most options leave you guessing. We give you the full picture first.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Home Warranties */}
            <Card className="border-2 border-gray-200 bg-gray-50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-bold text-gray-500 mb-2 text-lg">Home Warranties</h3>
                <p className="text-gray-500 text-sm mb-4">Pay annually. Fight claims. Reactive only.</p>
                <p className="text-gray-400 text-sm font-medium">$400-600/year + fees</p>
              </CardContent>
            </Card>

            {/* Valenrock - Highlighted */}
            <Card className="border-4 border-[#CFB973] bg-[#003366] shadow-2xl transform md:-translate-y-4 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-[#CFB973] text-[#003366] text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                  BETTER WAY
                </span>
              </div>
              <CardContent className="p-8 text-center pt-10">
                <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-4">
                  <BadgeCheck className="w-8 h-8 text-[#003366]" />
                </div>
                <h3 className="font-bold text-white mb-2 text-lg">Valenrock Assessment</h3>
                <p className="text-white/80 text-sm mb-4">Understand your home first. Plan from there.</p>
                <p className="text-[#CFB973] text-sm font-bold">One-time fee from $249</p>
              </CardContent>
            </Card>

            {/* Random Contractors */}
            <Card className="border-2 border-gray-200 bg-gray-50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-bold text-gray-500 mb-2 text-lg">Random Contractors</h3>
                <p className="text-gray-500 text-sm mb-4">Different person every time. No history.</p>
                <p className="text-gray-400 text-sm font-medium">$75-300 per visit</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Remodeling CTA */}
      <section className="py-16 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#003366] mb-4">Need Remodeling Too?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Valenrock Remodelers handles kitchens, bathrooms, basements, and full renovations. 
            Your Home Profile makes project planning easier — but remodeling is always optional.
          </p>
          <Link to="/remodeling">
            <Button size="lg" className="bg-[#003366] hover:bg-[#003366]/90 text-white font-semibold px-10 py-6 text-lg border-2 border-[#003366]">
              View Remodeling Services
            </Button>
          </Link>
        </div>
      </section>

      {/* Final CTA - Navy with Gold */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Let's Start the Conversation</h2>
          <p className="text-white/80 mb-8 text-lg max-w-2xl mx-auto">
            Tell us about your home. We'll handle the rest.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-12 py-7 text-xl shadow-xl hover:shadow-2xl transition-all">
                Join Assessment Waitlist
              </Button>
            </Link>
            <Link to="/remodeling">
              <Button size="lg" className="bg-transparent hover:bg-white/10 text-white font-bold px-12 py-7 text-xl border-2 border-[#CFB973]">
                Remodeling (Open Now)
              </Button>
            </Link>
          </div>
          <p className="text-white/60 text-sm mt-6">Assessment Waitlist • Remodeling Available Now</p>
          
          {/* Contact Info Bar */}
          <div className="mt-12 pt-8 border-t border-white/20 flex flex-col sm:flex-row justify-center items-center gap-6 text-white/70">
            <a href="tel:5513799774" className="flex items-center gap-2 hover:text-[#CFB973] transition-colors">
              <Phone size={18} />
              <span>(551) 379-9774</span>
            </a>
            <a href="mailto:contact@valenrockhome.services" className="flex items-center gap-2 hover:text-[#CFB973] transition-colors">
              <Mail size={18} />
              <span>contact@valenrockhome.services</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
